import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../models/api-response.model';

/** Client branding from the `site_layouts` table (subset used at bootstrap). */
export interface SiteLayout {
  primary_color?: string | null;
  link_color?: string | null;
  logo_url?: string | null;
  application_name?: string | null;
}

/**
 * Loads per-client branding and applies it site-wide as CSS custom properties
 * on <html> (per CLAUDE.md direction), so `primary_color` / `link_color` cascade
 * over the Daxa theme. Runs at app bootstrap via an app initializer.
 *
 * Fails silently: if the endpoint is unavailable the app keeps the default Daxa
 * theme rather than blocking startup.
 */
@Injectable({ providedIn: 'root' })
export class LayoutService {
  private readonly http = inject(HttpClient);

  loadBranding(): Observable<SiteLayout | null> {
    // Dormant until the API ships the endpoint — avoids a failing /layout call on every load.
    if (!environment.useLayoutApi) {
      return of(null);
    }
    // NOTE: confirm the final path with the API team — CLAUDE.md notes GET /api/layout.
    return this.http.get<ApiResponse<SiteLayout>>(`${environment.apiUrl}/layout`).pipe(
      map((res) => res?.data ?? null),
      tap((layout) => this.apply(layout)),
      catchError(() => of(null)),
    );
  }

  private apply(layout: SiteLayout | null | undefined): void {
    if (!layout) {
      return;
    }
    const root = document.documentElement;
    // primary_color → brand primary; link_color → Daxa's accent/link variable.
    if (layout.primary_color) {
      root.style.setProperty('--primaryColor', layout.primary_color);
    }
    if (layout.link_color) {
      root.style.setProperty('--daxaColor', layout.link_color);
    }
  }
}
